import { useState } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
  AlertDialogTrigger,
} from "./ui/alert-dialog";
import { CheckCircle2, Loader2 } from "lucide-react";

/**
 * CompleteBookingDialog — student confirms the job is done, optionally logging
 * extra hours the Move-in Pro worked beyond the estimate. Extras are billed
 * off-session at the same hourly rate on submit.
 */
export function CompleteBookingDialog({ booking, onCompleted }) {
  const [open, setOpen] = useState(false);
  const proposed = Number(booking?.proposed_extra_hours) || 0;
  const [extra, setExtra] = useState(String(proposed));
  const [submitting, setSubmitting] = useState(false);

  const extraHours = Math.max(0, Math.min(12, Number(extra) || 0));
  const adjusted = proposed > 0 && Math.abs(extraHours - proposed) > 0.001;

  const confirm = async () => {
    setSubmitting(true);
    try {
      await api.post(`/bookings/${booking.booking_id}/complete`, { extra_hours_worked: extraHours });
      toast.success(
        extraHours > 0
          ? `Marked as done — extra ${extraHours}h billed on completion.`
          : "Marked as done — leave a review below?"
      );
      setOpen(false);
      onCompleted?.(booking.booking_id);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not mark done");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <AlertDialog open={open} onOpenChange={setOpen}>
      <AlertDialogTrigger asChild>
        <Button size="sm" data-testid={`complete-${booking.booking_id}`}
          className="rounded-full bg-sage hover:bg-sage/90 text-midnight">
          <CheckCircle2 className="w-4 h-4 mr-1" /> Mark done
        </Button>
      </AlertDialogTrigger>
      <AlertDialogContent data-testid={`complete-dialog-${booking.booking_id}`}>
        <AlertDialogHeader>
          <AlertDialogTitle>Mark the job complete</AlertDialogTitle>
          <AlertDialogDescription asChild>
            <div className="text-charcoal/70 text-sm space-y-3">
              <p>
                Confirming will release the remaining <span className="font-medium text-charcoal">75% labor balance</span> to the card on file and trigger the payout.
              </p>
              <div className="rounded-lg border border-[#1F2A40] bg-[#0B1220] p-3 space-y-2">
                <Label htmlFor={`extra-${booking.booking_id}`} className="text-charcoal text-sm">
                  Extra hours worked beyond the estimate (optional)
                </Label>
                {proposed > 0 && (
                  <div className="text-[12px] text-cognac bg-cognac/10 rounded-md px-2 py-1.5 leading-snug" data-testid={`proposed-note-${booking.booking_id}`}>
                    Your Move-in Pro logged <strong>+{proposed}h</strong> worked. One-tap approve, or adjust below.
                  </div>
                )}
                <Input
                  id={`extra-${booking.booking_id}`}
                  data-testid={`complete-extra-${booking.booking_id}`}
                  type="number"
                  min="0"
                  max="12"
                  step="0.25"
                  value={extra}
                  onChange={(e) => setExtra(e.target.value)}
                  className="rounded-lg h-10"
                />
                {adjusted && (
                  <p className="text-[11px] text-terracotta leading-snug" data-testid={`adjusted-note-${booking.booking_id}`}>
                    You&apos;re adjusting the Pro&apos;s logged {proposed}h → {extraHours}h.
                  </p>
                )}
                <p className="text-[11px] text-charcoal/50 leading-snug">
                  Billed at the same hourly rate on completion. Leave at 0 if there were no extras.
                </p>
              </div>
            </div>
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel data-testid={`complete-back-${booking.booking_id}`}>Not yet</AlertDialogCancel>
          <AlertDialogAction
            onClick={(e) => { e.preventDefault(); confirm(); }}
            disabled={submitting}
            data-testid={`complete-confirm-${booking.booking_id}`}
            className="bg-sage hover:bg-sage/90 text-midnight disabled:opacity-50"
          >
            {submitting ? (<><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Completing…</>) : "Confirm & bill"}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
